import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const DIALOG_CLOSE_FALLBACK_MS = 700;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | null} */
  #showFrame = null;

  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#handleResize.cancel?.();

    if (this.#showFrame !== null) {
      cancelAnimationFrame(this.#showFrame);
      this.#showFrame = null;
    }

    const { dialog } = this.refs;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    unlockScroll(dialog);

    if (dialog.open) {
      dialog.close();
    }

    this.#isClosing = false;
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#isClosing) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    if (this.#showFrame !== null) cancelAnimationFrame(this.#showFrame);

    this.#showFrame = requestAnimationFrame(() => {
      this.#showFrame = null;

      if (!this.isConnected || !dialog.isConnected || dialog.open) return;

      try {
        lockScroll(dialog);
        dialog.showModal();
        this.dispatchEvent(new DialogOpenEvent());

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      } catch (_) {
        // Never leave the page locked if an app or a section update changed
        // the dialog between the click and this animation frame.
        unlockScroll(dialog);
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#showFrame !== null) {
      cancelAnimationFrame(this.#showFrame);
      this.#showFrame = null;
    }

    if (!dialog.open) {
      unlockScroll(dialog);
      return;
    }

    if (this.#isClosing) return;
    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await Promise.race([
        onAnimationEnd(dialog, undefined, { subtree: false }),
        new Promise((resolve) => window.setTimeout(resolve, DIALOG_CLOSE_FALLBACK_MS)),
      ]);
    } finally {
      // Cleanup must run even if animations are interrupted by an app render,
      // tab suspension, or a browser back-forward-cache transition.
      unlockScroll(dialog);
      if (dialog.open) dialog.close();

      try {
        scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      } catch (_) {
        // A replaced scroll container must not prevent dialog cleanup.
      }

      dialog.classList.remove('dialog-closing');
      dialog.style.removeProperty('animation');
      this.#isClosing = false;

      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);